import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  ConfigurationModel,
  ContentBlock,
  Headline,
  PublicPortalSettings,
  RouteStatusLayerConfig,
} from '../../../../shared/models/configuration.model';
import {firstValueFrom, Subscription} from 'rxjs';
import {ConfigurationService} from '../../../../configuration/configuration.service';
import {VehiclesService} from '../../../../data/vehicles/vehicles.service';
import {DomSanitizer} from '@angular/platform-browser';
import {ToastService} from '../../../../shared/services/toast.service';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '../../../../../environments/environment';
import {CurrentEditing} from '../../../../shared/tools/CurrentEditing';
import {MapLayerConfiguration} from '../../../../shared/models/map-layer.model';
import {RoutesService} from '../../../../data/routes/routes.service';
import {NamedId} from '../../../../shared/models/NamedId';
import {
  DialogCreateContentBlockComponent,
  DialogUpdateContentBlockComponent
} from './dialogs-content-block/content-block-dialog-components';
import {
  ConfirmationDialogComponent
} from '../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import {HeadlineComponent} from './headline/headline.component';
import {MatDivider} from '@angular/material/divider';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {CardContentDirective, CardContentRawDirective} from '../../../../shared/components/card/card-content.directive';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatTooltip} from '@angular/material/tooltip';
import {
  ToggleExpansionPanelComponent
} from '../../../../shared/components/toggle-expansion-panel/toggle-expansion-panel.component';
import {MatIcon} from '@angular/material/icon';
import {FormsModule} from '@angular/forms';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatRadioGroup} from '@angular/material/radio';
import {ContentBlockComponent} from './content-block/content-block.component';
import {MatSlider, MatSliderThumb} from '@angular/material/slider';
import {RowWithToggleComponent} from '../row-with-toggle/row-with-toggle.component';
import {
  DialogSingleFieldEditComponent,
  SingleFieldEditData
} from '../manage-vehicles/dialogs-vehicle/dialog-single-field-edit.component';
import {MatInput} from '@angular/material/input';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MapLayerPreviewComponent} from './map-layer-preview/map-layer-preview.component';
import {ChooseLayerDialogComponent} from './choose-layer-dialog/choose-layer-dialog.component';

interface VehicleGroup {
  id: number;
  name: string;
  selected: boolean;
}

@Component({
  imports: [
    MatButton,
    HeadlineComponent,
    MatDivider,
    MatProgressSpinner,
    CardComponent,
    CardContentDirective,
    MatIconButton,
    MatTooltip,
    CdkCopyToClipboard,
    MatIcon,
    MatLabel,
    CardContentRawDirective,
    ToggleExpansionPanelComponent,
    MatFormField,
    MatSelect,
    FormsModule,
    MatOption,
    MatRadioGroup,
    ContentBlockComponent,
    MatSlider,
    MatSliderThumb,
    RowWithToggleComponent,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatSlideToggle,
    MatMenuTrigger
  ],
  selector: 'app-manage-public-portal',
  styleUrls: [
    './manage-public-portal.component.scss',
    '../../../settings/settings-fields.scss',
    '../../../settings/settings-common.scss',
  ],
  templateUrl: './manage-public-portal.component.html'
})
export class ManagePublicPortalComponent implements OnInit, OnDestroy {

  configuration: ConfigurationModel;
  publicMapUrl: string;
  customerId: string;
  settings: PublicPortalSettings;
  headlines: Headline[];
  logo: string;
  vehicleGroups: VehicleGroup[];
  configSubscription: Subscription;
  routeConfigurations: NamedId[] = [{id: 0, name: 'None'}];
  servicedThresholdIndex: number;
  showAdditionalMapLayers: boolean;
  currentEditing: CurrentEditing = new CurrentEditing();
  currentLogo: string;

  mapLayers: MapLayerConfiguration[] = [];
  savingMapLayers = false;

  protected readonly RouteStatusLayerConfig = RouteStatusLayerConfig;

  savingLogo = false;

  constructor(protected configurationService: ConfigurationService,
              private readonly vehicleService: VehiclesService,
              private readonly routesService: RoutesService,
              private readonly domSanitizer: DomSanitizer,
              protected toast: ToastService,
              public dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.configSubscription = this.configurationService.sharedConfigurationModel.subscribe(model => {
      if (model) {
        this.configuration = model;
      }
    });

    this.configurationService.getLogo().then(response => {
      this.logo = response.data;
      this.currentLogo = this.logo;
      this.currentEditing.loaded('logo');
    });

    this.routesService.getRouteConfigurations().then(routeConfigurations => {
      this.routeConfigurations.push(...routeConfigurations.data.map(config => {
          return {
            id: config.id,
            name: config.name,
          };
        })
      );
    }).catch((message) => {
      this.toast.long('Failed to load Route Configurations!');
      console.error(message);
    });

    this.configurationService.getCustomerId().then(response => {
      this.customerId = response.data;
      this.updatePublicMapUrl();
    });

    this.configurationService.getPublicPortalSettings().then(response => {
      this.settings = response.data;
      this.headlines = this.settings.headline;
      this.servicedThresholdIndex = this.settings.routeStatusLayerConfig
        ? RouteStatusLayerConfig.THRESHOLD_VALUES.indexOf(this.settings.routeStatusLayerConfig.servicedThreshold) + 1
        : null;
      this.currentEditing.loaded('title');
      this.currentEditing.loaded('image-url');
      this.setExcludedVehicleGroups();
    });

    this.loadMapLayerConfigurations().then(mapLayers => {
      this.showAdditionalMapLayers = !!mapLayers;
    });

    this.vehicleService.getVehicleCategories()
      .then((response) => {
        this.vehicleGroups = response.map(group => {
          return {
            id: group.id,
            name: group.title,
            selected: false,
          };
        });
        this.setExcludedVehicleGroups();
      })
      .catch((message) => {
        this.toast.long('Failed to load Vehicle Groups!');
        console.error(message);
      });
  }

  async loadMapLayerConfigurations() {
    return this.configurationService.getPublicMapLayers()
      .then(layers => {
        this.mapLayers = layers;
        return this.mapLayers;
      });
  }

  ngOnDestroy(): void {
    if (this.configSubscription) {
      this.configSubscription.unsubscribe();
    }
  }

  getBase64Image() {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(this.currentLogo);
  }

  fileInputChange(event: any) {
    this.savingLogo = true;
    const file = event.target.files[0];
    const reader = new FileReader();
    const that = this;
    reader.onloadend = () => {
      that.currentLogo = reader.result.toString();
      this.saveLogo();
    };
    reader.readAsDataURL(file);
  }

  saveLogo() {
    this.logo = this.currentLogo;
    this.configurationService.updateLogo(this.logo).then(() => {
      this.toast.short('Logo Updated!');
    }).catch(error => {
      this.toast.long('Logo Update Failed! ' + error);
    }).finally(() => {
      this.savingLogo = false;
    });
  }

  createHeadline() {
    this.dialog.open(DialogSingleFieldEditComponent, {
      width: '400px',
      data: {
        title: 'Write new message',
        fieldName: 'Message Text',
        actionLabel: 'Add Message',
        action: async value => {
          const newHeadline = {text: value, selected: false} as Headline;
          if (this.headlines.length === 0) {
            newHeadline['selected'] = true;
          }
          this.headlines.push(newHeadline);

          this.settings.headline = this.headlines;
          await this.updatePublicPortalSettings('Message has been added');
          this.currentEditing.saved('headline');
        }
      } as SingleFieldEditData
    });
  }

  deleteHeadline(index: number) {
    this.headlines.splice(index, 1);
    // if selected was deleted, make selected the first from the array if it exists
    const selected = this.headlines.find(headline => headline.selected === true);
    if (!selected && this.headlines.length > 0) {
      this.headlines[0].selected = true;
    }

    this.settings.headline = this.headlines;
    this.updatePublicPortalSettings('Message has been removed').then();
  }

  updateHeadline(index: number, headline: Headline) {
    this.headlines[index] = headline;
    this.settings.headline = this.headlines;
    this.updatePublicPortalSettings('Message has been updated').then();
  }

  selectedHeadlineChanged(index: number, value: boolean) {
    this.headlines[index].selected = value;
    this.settings.headline = this.headlines;
    this.updatePublicPortalSettings().then();
  }

  updateExcludedVehicleGroups() {
    this.settings.excludeVehicleGroupIds = this.vehicleGroups.filter(group => !group.selected).map(group => group.id);
    this.updatePublicPortalSettings().then();
  }

  async updatePublicPortalSettings(msg: string = 'Settings updated.') {
    const result = await this.configurationService.updatePublicPortalSettings(this.settings);
    this.toast.short(msg);
    return result;
  }

  onVehicleHistoryChange() {
    if (this.settings.allowRouteStatusMap) {
      const servicedThreshold = 4;
      this.settings.routeStatusLayerConfig = {routeConfigurationId: 0, servicedThreshold};
      this.servicedThresholdIndex = RouteStatusLayerConfig.THRESHOLD_VALUES.indexOf(servicedThreshold) + 1;
    } else {
      this.settings.routeStatusLayerConfig = null;
    }
    this.updatePublicPortalSettings().then();
  }

  // we do not have access to component members here, so we pass it via parameter
  formatServicedThresholdLabel(values: number[]) {
    return (index: number): string => {
      return `${values[index - 1]}h`;
    };
  }

  onServicedThresholdChange(_) {
    this.settings.routeStatusLayerConfig.servicedThreshold = RouteStatusLayerConfig.THRESHOLD_VALUES[this.servicedThresholdIndex - 1];
    this.updatePublicPortalSettings().then();
  }

  toggleMessageBar() {
    this.settings.hideMessageBar = !this.settings.hideMessageBar;
    this.updatePublicPortalSettings().then();
  }

  toggleLeftPanel() {
    this.settings.hideLeftPanel = !this.settings.hideLeftPanel;
    this.updatePublicPortalSettings().then();
  }

  async toggleAdditionalMapLayers() {
    this.mapLayers.forEach(layer => {
        layer.publicEnabled = this.showAdditionalMapLayers;
      }
    );
    return this.saveMapLayers()
      .then(savedLayers => {
        this.mapLayers = savedLayers;
        this.toast.long(`Map layers have been ${this.showAdditionalMapLayers ? 'enabled' : 'disabled'}`);
      });
  }

  async saveMapLayers() {
    this.savingMapLayers = true;
    return this.configurationService
      .updatePublicMapLayers(this.mapLayers)
      .finally(() => {
        this.savingMapLayers = false;
      });
  }

  async removeMapLayer(index: number) {
    this.mapLayers.splice(index, 1);
    return this.saveMapLayers().then(savedLayers => {
      this.mapLayers = savedLayers;
      this.toast.long('Map layer has been deleted');
    });
  }

  async moveMapLayerDown(index: number) {
    if (index < 0 || index >= this.mapLayers.length - 1) {
      return Promise.resolve();
    }
    const firstItem = this.mapLayers[index];
    const secondItem = this.mapLayers[index + 1];
    const secondItemOrder = secondItem.publicOrder;

    secondItem.publicOrder = firstItem.publicOrder;
    firstItem.publicOrder = secondItemOrder;

    return this.saveMapLayers()
      .then(savedLayers => {
        this.mapLayers = savedLayers;
        this.toast.long('Map layer has been moved');
      });
  }

  getIframeCode() {
    return '<iframe id="publicMap"\n' +
      '        title="PlowOps Public Map"\n' +
      '        width="600"\n' +
      '        height="600"\n' +
      '        src="' + this.publicMapUrl + '">\n' +
      '</iframe>';
  }

  createContentBlock() {
    const dialogRef = this.dialog.open(DialogCreateContentBlockComponent, {
      width: '850px',
      maxWidth: '850px',
      data: new ContentBlock(),
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const contentBlock = dialogResult as ContentBlock;
        if (this.settings.contentBlocks.length === 0) {
          contentBlock.selected = true;
        }
        this.settings.contentBlocks.push(contentBlock);
        this.updatePublicPortalSettings().then();
      }
    });
  }

  updateContentBlock(index: number) {
    const contentBlock = {...this.settings.contentBlocks[index]}; // shallow copy
    const dialogRef = this.dialog.open(DialogUpdateContentBlockComponent, {
      width: '850px',
      maxWidth: '850px',
      data: contentBlock,
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const updatedContentBlock = dialogResult as ContentBlock;
        this.settings.contentBlocks.splice(index, 1, updatedContentBlock);
        this.updatePublicPortalSettings().then();
      }
    });
  }

  deleteContentBlock(index: number) {
    const contentBlock = this.settings.contentBlocks[index];
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        text: `Are you sure you want to delete the Content Block "${contentBlock.title}"?`
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.settings.contentBlocks.splice(index, 1);
        this.updatePublicPortalSettings().then();
      }
    });
  }

  selectContentBlock(index: number) {
    this.settings.contentBlocks.forEach(it => {
      delete it.selected;
    });
    this.settings.contentBlocks[index].selected = true;
    this.updatePublicPortalSettings().then();
  }

  private setExcludedVehicleGroups() {
    if (!!this.vehicleGroups && !!this.settings) {
      this.vehicleGroups.forEach(group => {
        group.selected = !this.settings.excludeVehicleGroupIds.includes(group.id);
      });
    }
  }

  private updatePublicMapUrl() {
    this.publicMapUrl = `${environment.publicBaseUrl}?id=${this.customerId}`;
  }

  onEnabledChange(mapLayer: MapLayerConfiguration) {
    mapLayer.publicEnabled = !mapLayer.publicEnabled;
    this.saveMapLayers().then();
  }


  openMapPreview() {
    this.mapLayers.forEach(mapLayer => {
      mapLayer.checked = mapLayer.publicEnabled;
    });

    this.dialog.open(MapLayerPreviewComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '80vw',
      height: '85vh',
      data: { mapLayers: this.mapLayers, customerId: this.customerId },
    });
  }

  addNewLayer() {
    const ref = this.dialog.open<ChooseLayerDialogComponent, any, MapLayerConfiguration>(ChooseLayerDialogComponent, {});

    firstValueFrom(ref.afterClosed()).then(result => {
      if (result) {
        result.publicEnabled = true;
        result.publicOrder = this.mapLayers
          .reduce((index, layer) => Math.max(index, layer.publicOrder), 0) + 1;

        const newMapLayers = [...this.mapLayers, result];
        this.configurationService.updatePublicMapLayers(newMapLayers)
          .then(layers => {
            this.mapLayers = layers;
            this.toast.long('Map layer has been added');
          })
          .catch(error => {
            console.error('Error adding map layer', error);
            this.toast.long('Error while adding map layer');
          });
      }
    });
  }
}
